<script>
import { mapGetters } from 'vuex';
import { shouldBeUrl } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import Avatar from 'next/avatar/Avatar.vue';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import FacebookReauthorize from './facebook/Reauthorize.vue';
import InstagramReauthorize from './channels/instagram/Reauthorize.vue';
import TiktokReauthorize from './channels/tiktok/Reauthorize.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import MicrosoftReauthorize from './channels/microsoft/Reauthorize.vue';
import GoogleReauthorize from './channels/google/Reauthorize.vue';
import WhatsappReauthorize from './channels/whatsapp/Reauthorize.vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import PreChatFormSettings from './PreChatForm/Settings.vue';
import WeeklyAvailability from './components/WeeklyAvailability.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import ConfigurationPage from './settingsPage/ConfigurationPage.vue';
import CustomerSatisfactionPage from './settingsPage/CustomerSatisfactionPage.vue';
import CollaboratorsPage from './settingsPage/CollaboratorsPage.vue';
import BotConfiguration from './components/BotConfiguration.vue';
import AccountHealth from './components/AccountHealth.vue';
import WhatsappTemplatesPage from './settingsPage/WhatsappTemplatesPage.vue';
import WhatsappTemplateUsagePage from './settingsPage/WhatsappTemplateUsagePage.vue';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import SenderNameExamplePreview from './components/SenderNameExamplePreview.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import GreetingInteractiveModal from './components/GreetingInteractiveModal.vue';
import Widget from 'dashboard/modules/widget-preview/components/Widget.vue';

export default {
  components: {
    BotConfiguration,
    CollaboratorsPage,
    ConfigurationPage,
    CustomerSatisfactionPage,
    FacebookReauthorize,
    GreetingsEditor,
    PreChatFormSettings,
    SettingIntroBanner,
    SettingsToggleSection,
    WhatsappTemplatesPage,
    WhatsappTemplateUsagePage,
    SettingsFieldSection,
    SettingsAccordion,
    WeeklyAvailability,
    SenderNameExamplePreview,
    MicrosoftReauthorize,
    GoogleReauthorize,
    NextButton,
    SpinnerLoader,
    InstagramReauthorize,
    TiktokReauthorize,
    WhatsappReauthorize,
    DuplicateInboxBanner,
    Editor,
    Avatar,
    ColorPicker,
    SelectInput,
    AccountHealth,
    Widget,
    GreetingInteractiveModal,
  },
  mixins: [inboxMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: '',
      greetingEnabled: true,
      greetingMessage: '',
      emailCollectEnabled: false,
      senderNameType: 'friendly',
      businessName: '',
      locktoSingleConversation: false,
      allowMessagesAfterResolved: true,
      continuityViaEmail: true,
      selectedInboxName: '',
      channelWebsiteUrl: '',
      webhookUrl: '',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      selectedFeatureFlags: [],
      replyTime: '',
      selectedTabIndex: 0,
      selectedPortalSlug: '',
      showBusinessNameInput: false,
      healthData: null,
      isLoadingHealth: false,
      healthError: null,
      widgetBubblePosition: 'right',
      widgetBubbleType: 'standard',
      widgetBubbleLauncherTitle: '',
      crmPipelineId: null,
      crmAutoCreateDeal: false,
      crmAutoAssignOwner: false,
      autoAssignOnOpen: false,
      reassignToLastAgent: false,
      reassignToContactOwner: false,
      reassignTimeoutMinutes: null,
      initialConversationStatus: 2,
      crmPipelines: [],
      windowKeeperEnabled: false,
      windowKeeperMessage: '',
      windowKeeperMinutesBefore: 60,
      windowKeeperMode: 'text',
      windowKeeperBody: '',
      windowKeeperFooter: '',
      windowKeeperButtons: [{ id: 'btn-1', title: '' }],
      windowKeeperStatuses: ['open'],
      // Greeting Interactive (URA)
      greetingMode: 'text',
      greetingInteractiveData: null,
      showGreetingInteractiveModal: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'inboxes/getUIFlags',
      portals: 'portals/allPortals',
    }),
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
    shouldShowWhatsAppConfiguration() {
      return this.isAWhatsAppCloudChannel;
    },
    whatsAppAPIProviderName() {
      if (this.isAWhatsAppCloudChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD');
      }
      if (this.is360DialogWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.360_DIALOG');
      }
      if (this.isATwilioWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO');
      }
      return '';
    },
    tabs() {
      let visibleToAllChannelTabs = [
        {
          key: 'inbox-settings',
          name: this.$t('INBOX_MGMT.TABS.SETTINGS'),
        },
        {
          key: 'collaborators',
          name: this.$t('INBOX_MGMT.TABS.COLLABORATORS'),
        },
      ];

      if (!this.isAVoiceChannel) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'business-hours',
            name: this.$t('INBOX_MGMT.TABS.BUSINESS_HOURS'),
          },
          {
            key: 'csat',
            name: this.$t('INBOX_MGMT.TABS.CSAT'),
          },
        ];
      }

      if (this.isAWebWidgetInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'pre-chat-form',
            name: this.$t('INBOX_MGMT.TABS.PRE_CHAT_FORM'),
          },
        ];
      }

      if (
        this.isATwilioChannel ||
        this.isALineChannel ||
        this.isAPIInbox ||
        this.isAVoiceChannel ||
        (this.isAnEmailChannel && !this.inbox.provider) ||
        this.shouldShowWhatsAppConfiguration ||
        this.isAWebWidgetInbox
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'configuration',
            name: this.$t('INBOX_MGMT.TABS.CONFIGURATION'),
          },
        ];
      }

      if (
        this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.AGENT_BOTS)
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'bot-configuration',
            name: this.$t('INBOX_MGMT.TABS.BOT_CONFIGURATION'),
          },
        ];
      }
      if (this.shouldShowWhatsAppConfiguration) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'whatsapp-health',
            name: this.$t('INBOX_MGMT.TABS.ACCOUNT_HEALTH'),
          },
          {
            key: 'whatsapp-templates',
            name: 'Meus Templates',
          },
          {
            key: 'template-usage',
            name: 'Uso de Templates',
          },
        ];
      }

      if (this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.CRM)) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'crm',
            name: 'CRM',
          },
        ];
      }

      return visibleToAllChannelTabs;
    },
    crmPipelineOptions() {
      return [
        { value: '', label: 'Nenhum (desvinculado)' },
        ...this.crmPipelines.map(p => ({ value: p.id, label: p.name })),
      ];
    },
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentInboxId);
    },
    inboxIcon() {
      const { medium, channel_type: type } = this.inbox;
      return getInboxIconByType(type, medium, 'line');
    },
    bannerMaxWidth() {
      const narrowTabs = [
        'collaborators',
        'configuration',
        'bot-configuration',
      ];
      if (narrowTabs.includes(this.selectedTabKey)) return 'max-w-4xl';
      if (this.selectedTabKey === 'inbox-settings') {
        return this.isAWebWidgetInbox ? 'max-w-7xl' : 'max-w-4xl';
      }
      return 'max-w-7xl';
    },
    inboxName() {
      if (this.isATwilioSMSChannel || this.isATwilioWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.messaging_service_sid || this.inbox.phone_number
          })`;
      }
      if (this.isAWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.phone_number})`;
      }
      if (this.isAnEmailChannel) {
        return `${this.inbox.name} (${this.inbox.email})`;
      }
      return this.inbox.name;
    },
    canLocktoSingleConversation() {
      return (
        this.isASmsInbox ||
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAPIInbox ||
        this.isATelegramChannel
      );
    },
    inboxNameLabel() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL');
    },
    inboxNamePlaceHolder() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER');
    },
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
    instagramUnauthorized() {
      return this.isAnInstagramChannel && this.inbox.reauthorization_required;
    },
    tiktokUnauthorized() {
      return this.isATiktokChannel && this.inbox.reauthorization_required;
    },
    // Check if a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return this.inbox.channel_type === INBOX_TYPES.FB && instagramInbox;
    },
    microsoftUnauthorized() {
      return this.isAMicrosoftInbox && this.inbox.reauthorization_required;
    },
    facebookUnauthorized() {
      return this.isAFacebookInbox && this.inbox.reauthorization_required;
    },
    googleUnauthorized() {
      const isLegacyInbox = ['imap.gmail.com', 'imap.google.com'].includes(
        this.inbox.imap_address
      );

      return (
        (this.isAGoogleInbox || isLegacyInbox) &&
        this.inbox.reauthorization_required
      );
    },
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappUnauthorized() {
      return (
        this.isAWhatsAppCloudChannel &&
        this.isEmbeddedSignupWhatsApp &&
        this.inbox.reauthorization_required
      );
    },
    whatsappRegistrationIncomplete() {
      if (
        !this.healthData ||
        !this.isAWhatsAppCloudChannel ||
        !this.isEmbeddedSignupWhatsApp
      ) {
        return false;
      }

      return (
        this.healthData.platform_type === 'NOT_APPLICABLE' ||
        this.healthData.throughput?.level === 'NOT_APPLICABLE'
      );
    },
    widgetBuilderStorageKey() {
      return `${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${this.inbox.id}`;
    },
  },
  watch: {
    $route(to, from) {
      if (to.name === 'settings_inbox_show') {
        const inboxChanged = to.params.inboxId !== from.params.inboxId;
        if (inboxChanged) {
          this.syncInboxData();
          this.setTabFromRouteParam();
        }
      }
    },
    inbox: {
      handler(newInbox, oldInbox) {
        if (newInbox?.id !== oldInbox?.id) {
          this.syncInboxData();
          this.fetchHealthData();
          this.$nextTick(() => {
            this.setTabFromRouteParam();
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchSharedData();
  },
  methods: {
    fetchSharedData() {
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      this.$store.dispatch('labels/get');
      this.$store.dispatch('portals/index');
      this.fetchCrmPipelines();
    },
    agentsList() {
      return this.$store.getters['agents/getAgents'] || [];
    },
    teamsList() {
      return this.$store.getters['teams/getTeams'] || [];
    },
    syncInboxData() {
      if (!this.inbox || !this.inbox.id) return;

      this.avatarUrl = this.inbox.avatar_url;
      this.selectedInboxName = this.inbox.name;
      this.webhookUrl = this.inbox.webhook_url;
      this.greetingEnabled = this.inbox.greeting_enabled || false;
      this.greetingMessage = this.inbox.greeting_message || '';
      this.emailCollectEnabled = this.inbox.enable_email_collect;
      this.senderNameType = this.inbox.sender_name_type;
      this.businessName = this.inbox.business_name;
      this.allowMessagesAfterResolved =
        this.inbox.allow_messages_after_resolved;
      this.continuityViaEmail = this.inbox.continuity_via_email;
      this.channelWebsiteUrl = this.inbox.website_url;
      this.channelWelcomeTitle = this.inbox.welcome_title;
      this.channelWelcomeTagline = this.inbox.welcome_tagline || '';
      this.selectedFeatureFlags = this.inbox.selected_feature_flags || [];
      this.replyTime = this.inbox.reply_time;
      this.locktoSingleConversation = this.inbox.lock_to_single_conversation;
      this.selectedPortalSlug = this.inbox.help_center
        ? this.inbox.help_center.slug
        : '';

      const savedBubbleSettings = LocalStorage.get(
        this.widgetBuilderStorageKey
      );
      if (savedBubbleSettings) {
        this.widgetBubblePosition = savedBubbleSettings.position || 'right';
        this.widgetBubbleType = savedBubbleSettings.type || 'standard';
        this.widgetBubbleLauncherTitle =
          savedBubbleSettings.launcherTitle || '';
      } else {
        this.widgetBubblePosition = 'right';
        this.widgetBubbleType = 'standard';
        this.widgetBubbleLauncherTitle = '';
      }

      this.crmPipelineId = this.inbox.crm_pipeline_id || null;
      this.crmAutoCreateDeal = this.inbox.crm_auto_create_deal || false;
      this.crmAutoAssignOwner = this.inbox.crm_auto_assign_owner || false;
      this.autoAssignOnOpen = this.inbox.auto_assign_on_open || false;
      this.reassignToLastAgent = this.inbox.reassign_to_last_agent || false;
      this.reassignToContactOwner = this.inbox.reassign_to_contact_owner || false;
      this.reassignTimeoutMinutes = this.inbox.reassign_timeout_minutes || null;
      this.initialConversationStatus = this.inbox.initial_conversation_status || 0;
      this.windowKeeperEnabled = this.inbox.window_keeper_enabled || false;
      this.windowKeeperMessage = this.inbox.window_keeper_message || '';
      this.windowKeeperMinutesBefore = this.inbox.window_keeper_minutes_before || 60;
      this.windowKeeperStatuses = this.inbox.window_keeper_statuses?.length ? [...this.inbox.window_keeper_statuses] : ['open'];
      const interactive = this.inbox.window_keeper_interactive;
      if (interactive && interactive.type === 'button') {
        this.windowKeeperMode = 'interactive';
        this.windowKeeperBody = interactive.body?.text || '';
        this.windowKeeperFooter = interactive.footer?.text || '';
        this.windowKeeperButtons = (interactive.action?.buttons || []).map((b, i) => ({
          id: b.reply?.id || `btn-${i + 1}`,
          title: b.reply?.title || '',
        }));
        if (!this.windowKeeperButtons.length) this.windowKeeperButtons = [{ id: 'btn-1', title: '' }];
      } else {
        this.windowKeeperMode = 'text';
        this.windowKeeperBody = '';
        this.windowKeeperFooter = '';
        this.windowKeeperButtons = [{ id: 'btn-1', title: '' }];
      }

      // Sync greeting interactive (URA)
      this.syncGreetingInteractive();
    },
    syncGreetingInteractive() {
      const gi = this.inbox.greeting_interactive;
      if (gi && gi.type) {
        this.greetingMode = 'interactive';
        this.greetingInteractiveData = gi;
      } else {
        this.greetingMode = 'text';
        this.greetingInteractiveData = null;
      }
    },
    onGreetingInteractiveSave(payload) {
      this.greetingInteractiveData = payload;
      this.showGreetingInteractiveModal = false;
      this.saveGreetingInteractive();
    },
    async fetchHealthData() {
      if (!this.inbox) return;

      if (!this.isAWhatsAppCloudChannel) {
        return;
      }

      try {
        this.isLoadingHealth = true;
        this.healthError = null;
        const response = await InboxHealthAPI.getHealthStatus(this.inbox.id);
        this.healthData = response.data;
      } catch (error) {
        this.healthError = error.message || 'Failed to fetch health data';
      } finally {
        this.isLoadingHealth = false;
      }
    },
    handleFeatureFlag(e) {
      this.selectedFeatureFlags = this.toggleInput(
        this.selectedFeatureFlags,
        e.target.value
      );
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
      this.updateRouteWithoutRefresh(selectedTabIndex);
    },
    updateRouteWithoutRefresh(selectedTabIndex) {
      const tab = this.tabs[selectedTabIndex];
      if (!tab) return;

      const { accountId, inboxId } = this.$route.params;
      const baseUrl = `/app/accounts/${accountId}/settings/inboxes/${inboxId}`;

      // Append the tab key only if it's not the default.
      const newUrl =
        tab.key === 'inbox-settings' ? baseUrl : `${baseUrl}/${tab.key}`;
      // Update URL without triggering route watcher
      window.history.replaceState(null, '', newUrl);
    },
    setTabFromRouteParam() {
      const { tab: tabParam } = this.$route.params;
      if (!tabParam) {
        this.selectedTabIndex = 0;
        return;
      }
      const tabIndex = this.tabs.findIndex(tab => tab.key === tabParam);
      this.selectedTabIndex = tabIndex === -1 ? 0 : tabIndex;
    },
    async updateInbox() {
      const bubbleSettings = {
        position: this.widgetBubblePosition,
        type: this.widgetBubbleType,
        launcherTitle: this.widgetBubbleLauncherTitle,
      };
      LocalStorage.set(this.widgetBuilderStorageKey, bubbleSettings);

      try {
        const payload = {
          id: this.currentInboxId,
          name: this.selectedInboxName?.trim(),
          enable_email_collect: this.emailCollectEnabled,
          allow_messages_after_resolved: this.allowMessagesAfterResolved,
          greeting_enabled: this.greetingEnabled,
          greeting_message: this.greetingMessage || '',
          greeting_interactive: this.greetingMode === 'interactive' && this.isAWhatsAppCloudChannel
            ? this.greetingInteractiveData
            : null,
          portal_id: this.selectedPortalSlug
            ? this.portals.find(
              portal => portal.slug === this.selectedPortalSlug
            )?.id || null
            : null,
          lock_to_single_conversation: this.locktoSingleConversation,
          sender_name_type: this.senderNameType,
          business_name: this.businessName || null,
          crm_pipeline_id: this.crmPipelineId || null,
          crm_auto_create_deal: this.crmAutoCreateDeal,
          auto_assign_on_open: this.autoAssignOnOpen,
          reassign_to_last_agent: this.reassignToLastAgent,
          reassign_to_contact_owner: this.reassignToContactOwner,
          reassign_timeout_minutes: this.reassignTimeoutMinutes ? Number(this.reassignTimeoutMinutes) : null,
          initial_conversation_status: this.initialConversationStatus,
          channel: {
            widget_color: this.inbox.widget_color,
            website_url: this.channelWebsiteUrl,
            webhook_url: this.webhookUrl,
            welcome_title: this.channelWelcomeTitle || '',
            welcome_tagline: this.channelWelcomeTagline || '',
            selectedFeatureFlags: this.selectedFeatureFlags,
            reply_time: this.replyTime || 'in_a_few_minutes',
            continuity_via_email: this.continuityViaEmail,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.showBusinessNameInput = false;
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxAvatar',
          this.currentInboxId
        );
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(this.$t('INBOX_MGMT.DELETE.API.AVATAR_SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('INBOX_MGMT.DELETE.API.AVATAR_ERROR_MESSAGE')
        );
      }
    },
    toggleSenderNameType(key) {
      this.senderNameType = key;
    },
    onClickShowBusinessNameInput() {
      this.showBusinessNameInput = true;
      this.$nextTick(() => {
        this.$refs.businessNameInput?.focus();
      });
    },
    hideBusinessNameInput() {
      this.showBusinessNameInput = false;
    },
    async fetchCrmPipelines() {
      try {
        await this.$store.dispatch('crmPipelines/get');
        this.crmPipelines = this.$store.getters['crmPipelines/getPipelines'] || [];
      } catch (e) {
        this.crmPipelines = [];
      }
    },
    async saveCrmSettings() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.currentInboxId,
          crm_pipeline_id: this.crmPipelineId || null,
          crm_auto_create_deal: this.crmAutoCreateDeal,
          crm_auto_assign_owner: this.crmAutoAssignOwner,
          auto_assign_on_open: this.autoAssignOnOpen,
          reassign_to_last_agent: this.reassignToLastAgent,
          reassign_to_contact_owner: this.reassignToContactOwner,
          reassign_timeout_minutes: this.reassignTimeoutMinutes ? Number(this.reassignTimeoutMinutes) : null,
          initial_conversation_status: this.initialConversationStatus,
        });
        useAlert('Configurações de CRM salvas com sucesso!');
      } catch (error) {
        useAlert(error.message || 'Erro ao salvar configurações de CRM.');
      }
    },
    async saveWindowKeeper() {
      try {
        const payload = {
          id: this.currentInboxId,
          window_keeper_enabled: this.windowKeeperEnabled,
          window_keeper_message: this.windowKeeperMode === 'text' ? this.windowKeeperMessage : (this.windowKeeperBody || ''),
          window_keeper_minutes_before: this.windowKeeperMinutesBefore,
          window_keeper_statuses: this.windowKeeperStatuses,
          window_keeper_interactive: null,
        };
        if (this.windowKeeperMode === 'interactive') {
          payload.window_keeper_interactive = {
            type: 'button',
            body: { text: this.windowKeeperBody.trim() },
            ...(this.windowKeeperFooter.trim() ? { footer: { text: this.windowKeeperFooter.trim() } } : {}),
            action: {
              buttons: this.windowKeeperButtons
                .filter(b => b.title.trim())
                .map((b, i) => ({
                  type: 'reply',
                  reply: { id: b.id || `btn-${i + 1}`, title: b.title.trim() },
                })),
            },
          };
        }
        await this.$store.dispatch('inboxes/updateInbox', { ...payload, formData: false });
        useAlert('Configurações de reativação de janela salvas com sucesso!');
      } catch (error) {
        useAlert(error.message || 'Erro ao salvar configurações de reativação de janela.');
      }
    },
    addWkButton() {
      if (this.windowKeeperButtons.length >= 3) return;
      this.windowKeeperButtons.push({ id: `btn-${this.windowKeeperButtons.length + 1}`, title: '' });
    },
    removeWkButton(idx) {
      if (this.windowKeeperButtons.length <= 1) return;
      this.windowKeeperButtons.splice(idx, 1);
    },
    toggleWindowKeeperStatus(status) {
      const idx = this.windowKeeperStatuses.indexOf(status);
      if (idx > -1) {
        // Don't allow removing the last status
        if (this.windowKeeperStatuses.length > 1) {
          this.windowKeeperStatuses.splice(idx, 1);
        }
      } else {
        this.windowKeeperStatuses.push(status);
      }
    },
    async saveGreetingInteractive() {
      try {
        const payload = {
          id: this.currentInboxId,
          formData: false,
          greeting_enabled: this.greetingEnabled,
          greeting_interactive: this.greetingMode === 'interactive'
            ? this.greetingInteractiveData
            : null,
          greeting_message: this.greetingMessage || '',
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert('Configurações de saudação salvas com sucesso!');
      } catch (error) {
        useAlert(error.message || 'Erro ao salvar configurações de saudação.');
      }
    },
  },
  validations: {
    webhookUrl: {
      shouldBeUrl,
    },
    selectedInboxName: {},
  },
};
</script>

<template>
  <div v-if="uiFlags.isFetching" class="flex items-center justify-center h-full w-full">
    <SpinnerLoader :size="28" class="text-n-blue-9" />
  </div>
  <div v-else class="grid grid-rows-[auto_1fr] h-full flex-grow flex-shrink pr-0 pl-0 w-full min-w-0 settings">
    <SettingIntroBanner :header-image="inbox.avatarUrl" :header-title="inboxName">
      <woot-tabs class="[&_ul]:p-0 top-px relative" :index="selectedTabIndex" :border="false" @change="onTabChange">
        <woot-tabs-item v-for="(tab, index) in tabs" :key="tab.key" :index="index" :name="tab.name" :show-badge="false"
          is-compact />
      </woot-tabs>
    </SettingIntroBanner>
    <section class="w-full overflow-auto py-8">
      <div class="max-w-7xl mx-auto w-full">
        <MicrosoftReauthorize v-if="microsoftUnauthorized" :inbox="inbox" class="mb-4" :class="bannerMaxWidth" />
        <FacebookReauthorize v-if="facebookUnauthorized" :inbox="inbox" class="mb-4" :class="bannerMaxWidth" />
        <GoogleReauthorize v-if="googleUnauthorized" :inbox="inbox" class="mb-4" :class="bannerMaxWidth" />
        <InstagramReauthorize v-if="instagramUnauthorized" :inbox="inbox" class="mb-4" :class="bannerMaxWidth" />
        <TiktokReauthorize v-if="tiktokUnauthorized" :inbox="inbox" class="mb-4" :class="bannerMaxWidth" />
        <WhatsappReauthorize v-if="whatsappUnauthorized"
          :whatsapp-registration-incomplete="whatsappRegistrationIncomplete" :inbox="inbox" class="mb-4"
          :class="bannerMaxWidth" />
        <DuplicateInboxBanner v-if="hasDuplicateInstagramInbox"
          :content="$t('INBOX_MGMT.ADD.INSTAGRAM.DUPLICATE_INBOX_BANNER')" class="mx-6 mb-4" :class="bannerMaxWidth" />

        <div v-if="selectedTabKey === 'inbox-settings'"
          class="flex flex-col md:flex-row items-center lg:items-start justify-between gap-5 lg:gap-10 mx-6">
          <div class="flex-1 flex flex-col min-w-0" :class="{
            'max-w-2xl': isAWebWidgetInbox,
            'max-w-4xl': !isAWebWidgetInbox,
          }">
            <div class="flex flex-col gap-1 items-start mb-4">
              <label class="text-heading-3 text-n-slate-12">
                {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_AVATAR.LABEL') }}
              </label>
              <Avatar :src="avatarUrl" :size="64" :icon-name="inboxIcon" name="" allow-upload rounded-full
                @upload="handleImageUpload" @delete="handleAvatarDelete" />
            </div>
            <SettingsFieldSection :label="inboxNameLabel">
              <woot-input v-model="selectedInboxName" class="[&>input]:!mb-0"
                :class="{ error: v$.selectedInboxName.$error }" :placeholder="inboxNamePlaceHolder" :error="v$.selectedInboxName.$error
                  ? $t('INBOX_MGMT.ADD.CHANNEL_NAME.ERROR')
                  : ''
                  " @blur="v$.selectedInboxName.$touch" />
            </SettingsFieldSection>
            <SettingsFieldSection v-if="isAPIInbox" :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.LABEL')
              ">
              <woot-input v-model="webhookUrl" class="[&>input]:!mb-0" :class="{ error: v$.webhookUrl.$error }"
                :placeholder="$t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.PLACEHOLDER'
                )
                  " :error="v$.webhookUrl.$error
                    ? $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.ERROR'
                    )
                    : ''
                    " @blur="v$.webhookUrl.$touch" />
            </SettingsFieldSection>

            <SettingsFieldSection v-if="isAWebWidgetInbox"
              :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')">
              <woot-input v-model="channelWebsiteUrl" class="[&>input]:!mb-0" :placeholder="$t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER'
              )
                " />
            </SettingsFieldSection>

            <SettingsFieldSection v-if="isAWhatsAppChannel" :label="$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.LABEL')">
              <input v-model="whatsAppAPIProviderName" type="text" disabled class="!mb-0" />
            </SettingsFieldSection>

            <SettingsFieldSection v-if="!isAVoiceChannel" :label="$t('INBOX_MGMT.HELP_CENTER.LABEL')"
              :help-text="$t('INBOX_MGMT.HELP_CENTER.SUB_TEXT')">
              <SelectInput v-model="selectedPortalSlug" :placeholder="$t('INBOX_MGMT.HELP_CENTER.PLACEHOLDER')"
                :options="[
                  { value: '', label: $t('INBOX_MGMT.HELP_CENTER.NONE') },
                  ...portals.map(p => ({ value: p.slug, label: p.name })),
                ]" />
            </SettingsFieldSection>

            <SettingsFieldSection v-if="isAWebWidgetInbox || isAnEmailChannel"
              :label="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.TITLE')"
              class="[&>div>div]:justify-end [&>div>div]:flex lg:[&>div:first-child]:h-12 [&>div:first-child]:h-16">
              <NextButton v-if="!showBusinessNameInput" ghost blue sm :label="$t(
                'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.BUTTON_TEXT'
              )
                " @click="onClickShowBusinessNameInput" />

              <div v-if="showBusinessNameInput" v-on-clickaway="hideBusinessNameInput"
                class="flex justify-end gap-2 w-full">
                <input ref="businessNameInput" v-model="businessName" :placeholder="$t(
                  'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.PLACEHOLDER'
                )
                  " class="!mb-0" type="text" />
                <NextButton :label="$t(
                  'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.SAVE_BUTTON_TEXT'
                )
                  " class="flex-shrink-0" @click="updateInbox" />
              </div>

              <template #extra>
                <SenderNameExamplePreview :sender-name-type="senderNameType" :business-name="businessName"
                  :is-website-channel="isAWebWidgetInbox" @update="toggleSenderNameType" />
              </template>
            </SettingsFieldSection>

            <SettingsAccordion v-if="isAWebWidgetInbox" :title="$t('INBOX_MGMT.WIDGET_FEATURES')" class="mt-6">
              <SettingsFieldSection :label="$t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL'
              )
                ">
                <woot-input v-model="channelWelcomeTitle" class="[&>input]:!mb-0" :placeholder="$t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
                )
                  " />
              </SettingsFieldSection>

              <SettingsFieldSection :label="$t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL'
              )
                " class="[&>div]:!items-start [&>div>label]:mt-1">
                <Editor v-model="channelWelcomeTagline" :placeholder="$t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
                )
                  " :max-length="255" channel-type="Context::InboxSettings" />
              </SettingsFieldSection>

              <SettingsFieldSection :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL')">
                <div class="justify-start">
                  <ColorPicker v-model="inbox.widget_color" />
                </div>
              </SettingsFieldSection>
              <SettingsFieldSection :label="$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE')
                ">
                <div class="flex items-center gap-6">
                  <div class="flex items-center gap-2">
                    <label class="text-n-slate-11 text-heading-3">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION_LABEL'
                        )
                      }}
                    </label>
                    <SelectInput v-model="widgetBubblePosition" :options="[
                      {
                        label: $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.LEFT'
                        ),
                        value: 'left',
                      },
                      {
                        label: $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.RIGHT'
                        ),
                        value: 'right',
                      },
                    ]" class="[&>select]:!p-0 min-w-16 [&>select]:!outline-none" />
                  </div>
                  <div class="h-3 w-px bg-n-weak rounded-lg" />
                  <div class="flex items-center gap-2">
                    <label class="text-n-slate-11 text-heading-3">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE_LABEL'
                        )
                      }}
                    </label>
                    <SelectInput v-model="widgetBubbleType" :options="[
                      {
                        label: $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.STANDARD'
                        ),
                        value: 'standard',
                      },
                      {
                        label: $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.EXPANDED_BUBBLE'
                        ),
                        value: 'expanded_bubble',
                      },
                    ]" class="[&>select]:!p-0 min-w-16 [&>select]:!outline-none" />
                  </div>
                </div>
              </SettingsFieldSection>

              <SettingsFieldSection :label="$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.LABEL'
              )
                ">
                <woot-input v-model="widgetBubbleLauncherTitle" :placeholder="$t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.PLACE_HOLDER'
                )
                  " class="[&>input]:!mb-0" />
              </SettingsFieldSection>
              <SettingsFieldSection :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.TITLE')" :help-text="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.HELP_TEXT')
                ">
                <SelectInput v-model="replyTime" :options="[
                  {
                    value: 'in_a_few_minutes',
                    label: $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_MINUTES'
                    ),
                  },
                  {
                    value: 'in_a_few_hours',
                    label: $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_HOURS'
                    ),
                  },
                  {
                    value: 'in_a_day',
                    label: $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_DAY'
                    ),
                  },
                ]" />
              </SettingsFieldSection>

              <SettingsFieldSection :label="$t('INBOX_MGMT.FEATURES.LABEL')"
                class="[&>div]:!items-start [&>div>label]:mt-2">
                <div class="flex flex-col gap-1 items-start">
                  <div class="flex gap-2 pt-2 py-0.5">
                    <input v-model="selectedFeatureFlags" type="checkbox" value="attachments"
                      class="w-4 h-4 rounded !bg-n-slate-3 dark:!bg-n-solid-3 border-n-weak text-n-blue-9 focus:ring-n-blue-4"
                      @input="handleFeatureFlag" />
                    <label for="attachments">
                      {{ $t('INBOX_MGMT.FEATURES.DISPLAY_FILE_PICKER') }}
                    </label>
                  </div>
                  <div class="flex gap-2 py-0.5">
                    <input v-model="selectedFeatureFlags" type="checkbox" value="emoji_picker"
                      class="w-4 h-4 rounded !bg-n-slate-3 dark:!bg-n-solid-3 border-n-weak text-n-blue-9 focus:ring-n-blue-4"
                      @input="handleFeatureFlag" />
                    <label for="emoji_picker">
                      {{ $t('INBOX_MGMT.FEATURES.DISPLAY_EMOJI_PICKER') }}
                    </label>
                  </div>
                  <div class="flex gap-2 py-0.5">
                    <input v-model="selectedFeatureFlags" type="checkbox" value="end_conversation"
                      class="w-4 h-4 rounded !bg-n-slate-3 dark:!bg-n-solid-3 border-n-weak text-n-blue-9 focus:ring-n-blue-4"
                      @input="handleFeatureFlag" />
                    <label for="end_conversation">
                      {{ $t('INBOX_MGMT.FEATURES.ALLOW_END_CONVERSATION') }}
                    </label>
                  </div>
                  <div class="flex gap-2 py-0.5">
                    <input v-model="selectedFeatureFlags" type="checkbox" value="use_inbox_avatar_for_bot"
                      class="w-4 h-4 rounded !bg-n-slate-3 dark:!bg-n-solid-3 border-n-weak text-n-blue-9 focus:ring-n-blue-4"
                      @input="handleFeatureFlag" />
                    <label for="use_inbox_avatar_for_bot">
                      {{ $t('INBOX_MGMT.FEATURES.USE_INBOX_AVATAR_FOR_BOT') }}
                    </label>
                  </div>
                </div>
              </SettingsFieldSection>
            </SettingsAccordion>

            <SettingsAccordion :title="$t('INBOX_MGMT.CHANNEL_PREFERENCES')" class="mt-6">
              <SettingsToggleSection v-model="greetingEnabled" :header="$t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL'
              )
                " :description="$t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
                )
                  ">
                <template v-if="greetingEnabled" #editor>
                  <div v-if="isAWhatsAppCloudChannel" class="flex flex-col gap-5">
                    <div>
                      <label class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider mb-2.5 block">Tipo de
                        saudação</label>
                      <div class="grid grid-cols-2 gap-2">
                        <button
                          class="py-2.5 px-4 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                          :class="greetingMode === 'text'
                            ? 'bg-n-blue-2 text-n-blue-11 ring-1 ring-n-blue-7'
                            : 'bg-n-alpha-2 text-n-slate-11 ring-1 ring-n-weak hover:bg-n-alpha-3 hover:ring-n-slate-7'"
                          @click="greetingMode = 'text'">
                          <span class="i-lucide-type size-4" />
                          Texto simples
                        </button>
                        <button
                          class="py-2.5 px-4 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                          :class="greetingMode === 'interactive'
                            ? 'bg-n-blue-2 text-n-blue-11 ring-1 ring-n-blue-7'
                            : 'bg-n-alpha-2 text-n-slate-11 ring-1 ring-n-weak hover:bg-n-alpha-3 hover:ring-n-slate-7'"
                          @click="greetingMode = 'interactive'">
                          <span class="i-lucide-route size-4" />
                          URA Interativa
                        </button>
                      </div>
                    </div>

                    <div v-if="greetingMode === 'text'">
                      <GreetingsEditor v-model="greetingMessage" :label="$t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                      )" :placeholder="$t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                      )" :richtext="false" />
                    </div>

                    <div v-if="greetingMode === 'interactive'" class="flex flex-col gap-4">
                      <div v-if="greetingInteractiveData" class="rounded-xl ring-1 ring-n-weak p-4">
                        <div class="flex items-center justify-between mb-2">
                          <div class="flex items-center gap-2">
                            <span class="i-lucide-route size-4 text-green-600" />
                            <span class="text-sm font-semibold text-n-slate-12">URA configurada</span>
                          </div>
                          <span
                            class="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium">
                            {{ greetingInteractiveData.type === 'button' ? 'Botões' : 'Lista' }}
                          </span>
                        </div>
                        <p class="text-xs text-n-slate-11 leading-relaxed line-clamp-2 mb-3">
                          {{ greetingInteractiveData.body?.text || '(sem corpo)' }}
                        </p>
                        <div v-if="greetingInteractiveData.type === 'button' && greetingInteractiveData.action?.buttons"
                          class="flex flex-wrap gap-1.5 mb-3">
                          <span v-for="(btn, idx) in greetingInteractiveData.action.buttons" :key="idx"
                            class="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-n-alpha-2 text-n-slate-11 ring-1 ring-n-weak">
                            {{ btn.reply?.title || `Botão ${idx + 1}` }}
                            <span v-if="btn.reply?.routing?.type" class="text-[9px] text-n-slate-9">
                              → {{ btn.reply.routing.type === 'agent' ? 'Agente' : 'Time' }}
                            </span>
                          </span>
                        </div>
                        <div v-if="greetingInteractiveData.type === 'list' && greetingInteractiveData.action?.sections"
                          class="mb-3">
                          <span class="text-[11px] text-n-slate-10">
                            {{greetingInteractiveData.action.sections.reduce((sum, s) => sum + (s.rows?.length || 0),
                              0)}} opções em {{ greetingInteractiveData.action.sections.length }} seção(ões)
                          </span>
                        </div>
                        <button
                          class="inline-flex items-center gap-2 text-sm font-medium text-green-600 hover:text-green-700 cursor-pointer transition-colors"
                          @click="showGreetingInteractiveModal = true">
                          <span class="i-lucide-pencil size-3.5" />
                          Editar URA
                        </button>
                      </div>

                      <div v-else
                        class="flex flex-col items-center gap-3 py-6 px-4 rounded-xl ring-1 ring-dashed ring-n-weak">
                        <span class="i-lucide-route size-8 text-n-slate-9" />
                        <p class="text-sm text-n-slate-10 text-center">
                          Nenhuma URA configurada ainda.<br />
                          Configure a mensagem interativa de saudação.
                        </p>
                        <button
                          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl cursor-pointer transition-colors shadow-sm"
                          @click="showGreetingInteractiveModal = true">
                          <span class="i-lucide-plus size-4" />
                          Configurar URA
                        </button>
                      </div>

                      <GreetingInteractiveModal :show="showGreetingInteractiveModal"
                        :initial-data="greetingInteractiveData" @close="showGreetingInteractiveModal = false"
                        @save="onGreetingInteractiveSave" />
                    </div>
                  </div>

                  <GreetingsEditor v-if="!isAWhatsAppCloudChannel" v-model="greetingMessage" :label="$t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                  )
                    " :placeholder="$t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                    )
                      " :richtext="!textAreaChannels" />
                </template>
              </SettingsToggleSection>

              <SettingsToggleSection v-if="isAWebWidgetInbox" v-model="emailCollectEnabled" :header="$t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX')
                " :description="$t(
                  'INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX_SUB_TEXT'
                )
                  " />

              <SettingsToggleSection v-if="isAWebWidgetInbox" v-model="allowMessagesAfterResolved" :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED')
                " :description="$t(
                  'INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED_SUB_TEXT'
                )
                  " />

              <SettingsToggleSection v-if="isAWebWidgetInbox" v-model="continuityViaEmail" :header="$t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL')
                " :description="$t(
                  'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL_SUB_TEXT'
                )
                  " />

              <SettingsToggleSection v-if="canLocktoSingleConversation" v-model="locktoSingleConversation" :header="$t('INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION')
                " :description="$t(
                  'INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION_SUB_TEXT'
                )
                  " />
            </SettingsAccordion>

            <SettingsAccordion :title="'Configurações de conversas'"
              :description="'Configure o status inicial e a atribuição automática das conversas recebidas neste inbox.'">
              <div class="mb-4">
                <label class="text-sm font-medium text-n-slate-12 mb-1 block">Status inicial da conversa</label>
                <p class="text-xs text-n-slate-10 mb-2">Escolha o status com que novas conversas chegam neste inbox.
                  Conversas com
                  bot ativo sempre iniciam como "Pendente", independente desta configuração.</p>
                <div class="flex gap-2 max-w-md">
                  <button
                    class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer flex items-center justify-center gap-1.5"
                    :class="initialConversationStatus === 0 ? 'bg-n-blue-9 text-white' : 'bg-n-alpha-2 text-n-slate-11 border border-n-weak hover:bg-n-alpha-3'"
                    @click="initialConversationStatus = 0">
                    <span class="i-lucide-circle-dot size-3.5" />
                    Aberta
                  </button>
                  <button
                    class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer flex items-center justify-center gap-1.5"
                    :class="initialConversationStatus === 2 ? 'bg-n-blue-9 text-white' : 'bg-n-alpha-2 text-n-slate-11 border border-n-weak hover:bg-n-alpha-3'"
                    @click="initialConversationStatus = 2">
                    <span class="i-lucide-clock size-3.5" />
                    Pendente
                  </button>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input v-model="autoAssignOnOpen" type="checkbox" class="sr-only peer" />
                  <div
                    class="w-11 h-6 !bg-n-slate-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:!bg-n-blue-9" />
                </label>
                <div>
                  <span class="text-sm font-medium text-n-slate-12">Atribuir ao agente que abrir a conversa</span>
                  <p class="text-xs text-n-slate-10">Quando um agente abre/aceita uma conversa, ele será automaticamente
                    atribuído
                    como responsável.</p>
                </div>
              </div>

              <div class="flex items-center gap-3 mt-3">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input v-model="reassignToContactOwner" type="checkbox" class="sr-only peer" />
                  <div
                    class="w-11 h-6 !bg-n-slate-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:!bg-n-blue-9" />
                </label>
                <div>
                  <span class="text-sm font-medium text-n-slate-12">Reatribuir ao responsável do contato</span>
                  <p class="text-xs text-n-slate-10">Quando um contato envia uma nova mensagem, a conversa será
                    atribuída ao agente responsável do contato. Tem <strong>prioridade</strong> sobre a
                    reatribuição ao último agente e sobre as filas de atribuição automática.</p>
                </div>
              </div>

              <div class="flex items-center gap-3 mt-3">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input v-model="reassignToLastAgent" type="checkbox" class="sr-only peer" />
                  <div
                    class="w-11 h-6 !bg-n-slate-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:!bg-n-blue-9" />
                </label>
                <div>
                  <span class="text-sm font-medium text-n-slate-12">Reatribuir ao último agente</span>
                  <p class="text-xs text-n-slate-10">Quando um contato envia uma nova mensagem (após a conversa anterior
                    ser
                    resolvida), a nova conversa será automaticamente atribuída ao último agente que atendeu esse contato
                    nesta
                    inbox. Aplica-se somente quando o owner do contato não é encontrado ou não é membro desta inbox.</p>
                </div>
              </div>

              <div class="flex items-start gap-3 mt-3">
                <label class="relative inline-flex items-center cursor-pointer mt-0.5 flex-shrink-0">
                  <input type="checkbox" class="sr-only peer" :checked="!!reassignTimeoutMinutes"
                    @change="reassignTimeoutMinutes = $event.target.checked ? 30 : null" />
                  <div
                    class="w-11 h-6 !bg-n-slate-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:!bg-n-blue-9" />
                </label>
                <div class="flex-1">
                  <span class="text-sm font-medium text-n-slate-12">Reatribuir por tempo de primeira resposta</span>
                  <p class="text-xs text-n-slate-10">Se o agente não responder dentro do tempo configurado, a conversa
                    será
                    transferida para outro agente do mesmo time.</p>
                  <div v-if="reassignTimeoutMinutes" class="flex items-center gap-2 mt-2">
                    <input v-model.number="reassignTimeoutMinutes" type="number" min="1" max="1440"
                      class="w-20 px-2 py-1 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:border-n-blue-9 focus:ring-2 focus:ring-n-blue-4 outline-none" />
                    <span class="text-xs text-n-slate-10">minutos</span>
                  </div>
                </div>
              </div>
            </SettingsAccordion>

            <SettingsAccordion v-if="isAWhatsAppCloudChannel" title="Reativação de janela (24h)" class="mt-6">
              <div class="flex flex-col gap-4">
                <p class="text-sm text-n-slate-10">
                  Envia uma mensagem personalizada antes da janela de 24h do WhatsApp expirar, para tentar manter a
                  conversa ativa.
                </p>
                <div class="flex items-center gap-3">
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input v-model="windowKeeperEnabled" type="checkbox" class="sr-only peer" />
                    <div
                      class="w-11 h-6 !bg-n-slate-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:!bg-n-blue-9" />
                  </label>
                  <div>
                    <span class="text-sm font-medium text-n-slate-12">Ativar reativação de janela</span>
                    <p class="text-xs text-n-slate-10">Enviar uma mensagem automática antes da janela de 24h fechar para
                      tentar
                      manter a conversa ativa.</p>
                  </div>
                </div>
                <div v-if="windowKeeperEnabled" class="flex flex-col gap-4">
                  <div>
                    <label class="text-sm font-medium text-n-slate-12 mb-1 block">Minutos antes do encerramento</label>
                    <p class="text-xs text-n-slate-10 mb-2">Quantos minutos antes da janela de 24h fechar a mensagem
                      será enviada.
                    </p>
                    <select v-model.number="windowKeeperMinutesBefore"
                      class="w-full max-w-md px-3 py-2 border border-n-weak rounded-lg text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option :value="30">30 minutos</option>
                      <option :value="60">1 hora</option>
                      <option :value="120">2 horas</option>
                      <option :value="180">3 horas</option>
                      <option :value="240">4 horas</option>
                      <option :value="360">6 horas</option>
                    </select>
                  </div>

                  <div>
                    <label class="text-sm font-medium text-n-slate-12 mb-1 block">Status das conversas</label>
                    <p class="text-xs text-n-slate-10 mb-2">Em quais status de conversa o deve atuar.</p>
                    <div class="flex gap-4">
                      <label class="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" value="open" :checked="windowKeeperStatuses.includes('open')"
                          class="w-4 h-4 rounded !bg-n-slate-3 dark:!bg-n-solid-3 border-n-weak text-n-blue-9 focus:ring-n-blue-4"
                          @change="toggleWindowKeeperStatus('open')" />
                        <span class="text-sm text-n-slate-12">Aberto</span>
                      </label>
                      <label class="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" value="pending" :checked="windowKeeperStatuses.includes('pending')"
                          class="w-4 h-4 rounded !bg-n-slate-3 dark:!bg-n-solid-3 border-n-weak text-n-blue-9 focus:ring-n-blue-4"
                          @change="toggleWindowKeeperStatus('pending')" />
                        <span class="text-sm text-n-slate-12">Pendente</span>
                      </label>
                    </div>
                    <p v-if="!windowKeeperStatuses.length" class="text-xs text-red-500 mt-1">Selecione ao menos um
                      status.</p>
                  </div>

                  <div>
                    <label class="text-sm font-medium text-n-slate-12 mb-2 block">Tipo de mensagem</label>
                    <div class="flex gap-2">
                      <button class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer"
                        :class="windowKeeperMode === 'text' ? 'bg-n-blue-9 text-white' : 'bg-n-alpha-2 text-n-slate-11 border border-n-weak hover:bg-n-alpha-3'"
                        @click="windowKeeperMode = 'text'">
                        <span class="i-lucide-type size-3.5 inline-block align-middle mr-1" />
                        Texto simples
                      </button>
                      <button class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer"
                        :class="windowKeeperMode === 'interactive' ? 'bg-n-blue-9 text-white' : 'bg-n-alpha-2 text-n-slate-11 border border-n-weak hover:bg-n-alpha-3'"
                        @click="windowKeeperMode = 'interactive'">
                        <span class="i-lucide-square size-3.5 inline-block align-middle mr-1" />
                        Mensagem interativa (Botões)
                      </button>
                    </div>
                  </div>

                  <div v-if="windowKeeperMode === 'text'">
                    <label class="text-sm font-medium text-n-slate-12 mb-1 block">Mensagem</label>
                    <p class="text-xs text-n-slate-10 mb-2">Mensagem que será enviada ao cliente para tentar manter a
                      janela de
                      conversa aberta.</p>
                    <textarea v-model="windowKeeperMessage" rows="3"
                      class="w-full max-w-lg px-3 py-2 border border-n-weak rounded-lg text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Ex: Olá! Só verificando se você precisa de mais alguma coisa. Posso ajudar em algo?" />
                  </div>

                  <div v-if="windowKeeperMode === 'interactive'" class="flex flex-col gap-4">
                    <div class="flex gap-4">
                      <div class="flex-1 flex flex-col gap-3">
                        <div>
                          <label class="text-xs font-medium text-n-slate-10 mb-1 block">Corpo da mensagem *</label>
                          <textarea v-model="windowKeeperBody" rows="3"
                            class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            placeholder="Olá! Sua janela de atendimento está prestes a expirar. Deseja continuar a conversa?"
                            maxlength="1024" />
                          <span class="text-[10px] text-n-slate-9">{{ (windowKeeperBody || '').length }}/1024</span>
                        </div>
                        <div>
                          <label class="text-xs font-medium text-n-slate-10 mb-1 block">Rodapé <span
                              class="text-n-slate-9">(opcional, max 60)</span></label>
                          <input v-model="windowKeeperFooter"
                            class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Texto de rodapé" maxlength="60" />
                        </div>
                        <div class="border border-n-weak rounded-lg p-3">
                          <div class="flex items-center justify-between mb-2">
                            <p
                              class="text-xs font-bold text-n-slate-12 uppercase tracking-wider flex items-center gap-1.5">
                              <span class="i-lucide-square size-3.5 text-n-blue-9" /> Botões de Resposta
                            </p>
                            <button v-if="windowKeeperButtons.length < 3"
                              class="text-[11px] font-medium cursor-pointer text-n-blue-9 hover:text-n-blue-11"
                              @click="addWkButton">
                              + Adicionar botão
                            </button>
                          </div>
                          <p class="text-[10px] text-n-slate-10 mb-2">Mínimo 1, máximo 3 botões. Cada título até 20
                            caracteres.</p>
                          <div v-for="(btn, idx) in windowKeeperButtons" :key="idx"
                            class="flex items-center gap-2 mb-2">
                            <input v-model="btn.title"
                              class="flex-1 px-3 py-1.5 border border-n-weak rounded-lg text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              :placeholder="`Botão ${idx + 1}`" maxlength="20" />
                            <span class="text-[10px] text-n-slate-9 w-8 text-right">{{ btn.title.length }}/20</span>
                            <button v-if="windowKeeperButtons.length > 1"
                              class="text-n-slate-10 hover:text-red-500 cursor-pointer" @click="removeWkButton(idx)">
                              <span class="i-lucide-trash-2 size-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="w-60 flex-shrink-0">
                        <p class="text-[10px] font-bold text-n-slate-10 uppercase tracking-wider mb-2">Preview</p>
                        <div class="rounded-xl overflow-hidden" style="background: #e5ddd5;">
                          <div class="p-2.5 min-h-[120px]">
                            <div class="rounded-lg p-2.5 max-w-[220px] shadow-sm" style="background: #dcf8c6;">
                              <p class="text-xs leading-relaxed whitespace-pre-wrap" style="color: #303030;">
                                {{ windowKeeperBody || 'Corpo da mensagem...' }}
                              </p>
                              <p v-if="windowKeeperFooter" class="text-[10px] mt-1.5" style="color: #8696a0;">{{
                                windowKeeperFooter
                              }}</p>
                            </div>
                            <div class="mt-1 max-w-[220px]">
                              <div v-for="(btn, idx) in windowKeeperButtons.filter(b => b.title.trim())" :key="idx"
                                class="rounded-lg py-1.5 px-2.5 text-center text-[11px] font-medium mt-1 shadow-sm"
                                style="background: #fff; color: #00a884;">
                                {{ btn.title }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <button
                      class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer"
                      style="background: #3b82f6; border: none;" @click="saveWindowKeeper">
                      Salvar configurações
                    </button>
                  </div>
                </div>
              </div>
            </SettingsAccordion>

            <div class="w-full flex justify-end items-center py-4 mt-2">
              <NextButton v-if="isAPIInbox" type="submit" :disabled="v$.webhookUrl.$invalid"
                :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')" :is-loading="uiFlags.isUpdating" @click="updateInbox" />
              <NextButton v-else type="submit" :disabled="v$.$invalid" :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
                :is-loading="uiFlags.isUpdating" @click="updateInbox" />
            </div>
          </div>

          <div v-if="isAWebWidgetInbox" class="flex-1 sticky top-4 self-start max-w-lg flex-shrink-0 w-full min-w-0">
            <div
              class="flex flex-col outline -outline-offset-1 outline-1 outline-n-weak w-full px-3 pt-3 pb-8 bg-n-surface-1 rounded-2xl min-h-[45rem] overflow-hidden">
              <Widget :welcome-heading="channelWelcomeTitle" :welcome-tagline="channelWelcomeTagline"
                :website-name="selectedInboxName" :logo="avatarUrl" is-online :reply-time="replyTime"
                :color="inbox.widget_color" :widget-bubble-position="widgetBubblePosition"
                :widget-bubble-launcher-title="widgetBubbleLauncherTitle" :widget-bubble-type="widgetBubbleType"
                :web-widget-script="inbox.web_widget_script" />
            </div>
          </div>
        </div>

        <div v-if="selectedTabKey === 'collaborators'" class="mx-6 max-w-4xl">
          <CollaboratorsPage :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'configuration'" class="mx-6 max-w-4xl">
          <ConfigurationPage :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'csat'">
          <CustomerSatisfactionPage :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'pre-chat-form'">
          <PreChatFormSettings :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'business-hours'">
          <WeeklyAvailability :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'bot-configuration'">
          <BotConfiguration :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'whatsapp-health'">
          <AccountHealth :health-data="healthData" />
        </div>
        <div v-if="selectedTabKey === 'whatsapp-templates'">
          <WhatsappTemplatesPage :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'template-usage'">
          <WhatsappTemplateUsagePage :inbox="inbox" />
        </div>
        <div v-if="selectedTabKey === 'crm'" class="mx-6 max-w-4xl">
          <div class="flex flex-col gap-6">
            <div>
              <h3 class="text-lg font-semibold text-n-slate-12 mb-1">Configurações de CRM</h3>
              <p class="text-sm text-n-slate-10">Configure o comportamento de atribuição automática de responsáveis (carteira) para este inbox.</p>
            </div>
            <div class="flex flex-col gap-4">
              <!-- Oculto: migrado para Automações CRM → Gatilho "Nova Conversa" -->
              <div v-if="false">
              <div>
                <label class="text-sm font-medium text-n-slate-12 mb-1 block">Funil do CRM</label>
                <p class="text-xs text-n-slate-10 mb-2">Selecione em qual funil as oportunidades deste inbox serão
                  criadas.</p>
                <select v-model="crmPipelineId"
                  class="w-full max-w-md px-3 py-2 border border-n-weak rounded-lg text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option :value="null">Nenhum (desvinculado)</option>
                  <option v-for="p in crmPipelines" :key="p.id" :value="p.id">
                    {{ p.name }}
                  </option>
                </select>
              </div>
              <div class="flex items-center gap-3">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input v-model="crmAutoCreateDeal" type="checkbox" class="sr-only peer" />
                  <div
                    class="w-11 h-6 !bg-n-slate-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:!bg-n-blue-9" />
                </label>
                <div>
                  <span class="text-sm font-medium text-n-slate-12">Criar oportunidade automaticamente</span>
                  <p class="text-xs text-n-slate-10">Ao receber uma nova conversa neste inbox, uma oportunidade será
                    criada
                    automaticamente no funil selecionado.</p>
                </div>
              </div>
              </div>
              <div class="flex items-center gap-3">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input v-model="crmAutoAssignOwner" type="checkbox" class="sr-only peer" />
                  <div
                    class="w-11 h-6 !bg-n-slate-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:!bg-n-blue-9" />
                </label>
                <div>
                  <span class="text-sm font-medium text-n-slate-12">Atribuir carteira automaticamente</span>
                  <p class="text-xs text-n-slate-10">Quando uma conversa for atribuída a um agente e o contato ainda não
                    tiver
                    um responsável, o agente será definido como responsável do contato automaticamente.</p>
                </div>
              </div>


            </div>
            <div>
              <button
                class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer"
                style="background: #3b82f6; border: none;" @click="saveCrmSettings">
                Salvar configurações de CRM
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
